import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Loader2, Moon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Textarea } from "~/components/ui/textarea";
import type { PatientResponseDto } from "~/lib/api";
import {
  fetchOvernightUpdate,
  saveOvernightUpdate,
  toOvernightFormValues,
} from "~/lib/overnight-updates";
import { fetchCurrentUserId, fetchPatient } from "~/lib/patients";
import { canRecordOvernightUpdate } from "~/lib/permissions";
import NotFoundRoute from "~/routes/not-found";
import {
  EMPTY_OVERNIGHT_FORM,
  overnightUpdateFormSchema,
  type OvernightUpdateFormValues,
} from "~/session/overnight-update/schema";
import { useSession } from "~/session/session-context";

type ScreenState =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  | { kind: "forbidden" }
  | { kind: "ready"; patient: PatientResponseDto };

// The four SBAR blocks, in order — shown read-only as the original handover
// context the overnight update is written against (mirrors the patient detail
// screen's labels).
const SBAR_BLOCKS: readonly {
  key: keyof PatientResponseDto["sbar"];
  letter: string;
  label: string;
}[] = [
  { key: "situation", letter: "S", label: "Situation" },
  { key: "background", letter: "B", label: "Background" },
  { key: "assessment", letter: "A", label: "Assessment" },
  { key: "recommendation", letter: "R", label: "Recommendation" },
];

/**
 * The overnight-update form (V2-E8.1, HAN-68): the incoming doctor's end-of-shift
 * note per patient — what happened overnight, what was done, and what the day
 * team needs to know — which feeds the next shift's morning handover. The
 * patient's original SBAR is shown read-only alongside the editable fields so
 * the doctor writes against the handover context without overwriting it.
 *
 * Rendered inside `<SessionLayout>`, so the session (and the caller's ward
 * access) is already resolved. Only the session's incoming doctor may save
 * ({@link canRecordOvernightUpdate}); the API enforces the same.
 */
export function OvernightUpdateScreen(): JSX.Element {
  const { session } = useSession();
  const { id: sessionId, patientId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  const form = useForm<OvernightUpdateFormValues>({
    resolver: zodResolver(overnightUpdateFormSchema),
    defaultValues: EMPTY_OVERNIGHT_FORM,
    mode: "onBlur",
  });
  const { reset } = form;

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      if (!sessionId || !patientId) {
        setState({ kind: "not-found" });
        return;
      }
      setState({ kind: "loading" });

      const [currentUserId, patientResult, overnightResult] = await Promise.all([
        fetchCurrentUserId(),
        fetchPatient(sessionId, patientId),
        fetchOvernightUpdate(sessionId, patientId),
      ]);
      if (signal.cancelled) return;

      if (patientResult.status === "not-found" || overnightResult.status === "not-found") {
        setState({ kind: "not-found" });
        return;
      }
      if (patientResult.status === "error") {
        setState({ kind: "error", message: patientResult.message });
        return;
      }
      if (overnightResult.status === "error") {
        setState({ kind: "error", message: overnightResult.message });
        return;
      }

      const user = currentUserId ? { id: currentUserId } : null;
      if (!canRecordOvernightUpdate(user, session)) {
        setState({ kind: "forbidden" });
        return;
      }

      // Pre-populate from the saved update, or start blank.
      reset(
        overnightResult.update
          ? toOvernightFormValues(overnightResult.update)
          : EMPTY_OVERNIGHT_FORM,
      );
      setState({ kind: "ready", patient: patientResult.data });
    },
    [patientId, reset, session, sessionId],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  const onSubmit = async (values: OvernightUpdateFormValues): Promise<void> => {
    if (!sessionId || !patientId) return;
    const result = await saveOvernightUpdate(sessionId, patientId, values);
    if (result.status === "ok") {
      reset(toOvernightFormValues(result.update));
      toast.success("Overnight update saved.");
      return;
    }
    if (result.status === "forbidden") {
      toast.error("Only the incoming doctor can record the overnight update.");
      return;
    }
    if (result.status === "not-found") {
      toast.error("This patient is no longer available on the handover.");
      navigate(`/session/${sessionId}/patient/${patientId}`);
      return;
    }
    toast.error(result.message);
  };

  if (state.kind === "loading") {
    return (
      <div className="flex items-center justify-center py-16" aria-live="polite">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading overnight update"
        />
      </div>
    );
  }

  if (state.kind === "not-found") {
    return <NotFoundRoute />;
  }

  const backLink = (
    <Link
      to={`/session/${sessionId}/patient/${patientId}`}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Back to patient
    </Link>
  );

  if (state.kind === "forbidden") {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        {backLink}
        <Alert>
          <AlertTitle>You can&rsquo;t record this overnight update</AlertTitle>
          <AlertDescription>
            Only the incoming doctor on this handover can record the overnight update.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        {backLink}
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{state.message}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setReloadKey((k) => k + 1);
              }}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { patient } = state;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {backLink}

      <header className="space-y-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Moon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          Overnight update
        </h1>
        <p className="text-sm text-muted-foreground">
          {patient.fullName} · Bed {patient.bedNumber}
        </p>
        <p className="text-sm text-muted-foreground">
          Record what happened with this patient overnight, for the morning handover. The original
          SBAR below is kept for context and isn&rsquo;t changed.
        </p>
      </header>

      <section className="space-y-4">
        <h2 className="text-lg font-medium">Original SBAR</h2>
        <dl className="space-y-4">
          {SBAR_BLOCKS.map((block) => (
            <div key={block.key} className="rounded-lg border border-border bg-muted/40 p-4">
              <dt className="text-sm font-semibold tracking-tight">
                <span className="text-muted-foreground">{block.letter} — </span>
                {block.label}
              </dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {patient.sbar[block.key]}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-medium">Overnight update</h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="whatHappened"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What happened overnight</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      placeholder="e.g. Spiked a temperature at 02:00, blood cultures sent."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="whatWasDone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What was done</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      placeholder="e.g. Started IV co-amoxiclav, repeat bloods in the morning."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dayTeamNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What the day team needs to know</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder="Optional — e.g. Chase the 08:00 CRP and review antibiotics on the round."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>Optional.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              Save overnight update
            </Button>
          </form>
        </Form>
      </section>
    </div>
  );
}
